/**
 * The table query for one consumer-local regular-column table (ADR 0020, AC-SOURCE-002).
 *
 * The definition is the watchlist's: an inline watchlist idea listing the requested symbols, with
 * the stream's stable `userViewKey` on the idea and output stages, so a re-post with a changed
 * symbol list amends the server view in place and answers with the same table key (CN-IMPL-004).
 * The output carries the symbol column, which keys every row, and the requested template columns
 * under their template ids, so row field ids match the grid's field ids.
 */
import type { Idea } from '@thinkalpha/platform-ws-client/contracts/ideas/index.js';
import type { ColumnOptions } from '@thinkalpha/platform-ws-client/contracts/table.js';
import type { KeyedTableRequest } from '@thinkalpha/ui-keyed-columns/tableColumnProvider';
import type { TemplateColumn } from '@thinkalpha/ui-types/contracts/column-template';
import { createTable } from 'src/lib/table';
import { IdeaStage, OutputStage, SourceStage } from 'src/lib/tableStages';
import type { TableCreationQuery } from 'src/models/TableModel';
import type { FetchableTableCreationResultWithColumns } from 'src/types';

export interface RegularColumnsQueryParams {
    readonly request: KeyedTableRequest<string, string>;
    readonly userViewKey: string;
    /** Template columns by id; a requested id without a column is not requested. */
    readonly columns: ReadonlyMap<string, TemplateColumn>;
    /** The keys table's symbol column, output so each row carries its key. */
    readonly symbolColumn: TemplateColumn;
    readonly columnOptions: Partial<ColumnOptions>;
}

/**
 * What a column computes under its id, ignoring presentation (name, width, visibility). Part of
 * the query key, so a changed definition is re-posted.
 */
export function columnDefinitionKey(column: TemplateColumn): string {
    const definition =
        column.type === 'formula'
            ? JSON.stringify([column.formula, column.imports])
            : column.type === 'indicator'
              ? column.indicator.id
              : `client:${column.clientKey}`;
    return `${column.id}::${definition}`;
}

export function regularColumnsQuery({
    request,
    userViewKey,
    columns,
    symbolColumn,
    columnOptions,
}: RegularColumnsQueryParams): TableCreationQuery {
    // Sorted copies: the same demand in another order is the same table.
    const symbols = [...request.keys].sort();
    const requested = request.fields
        .map((field) => columns.get(field))
        .filter((column): column is TemplateColumn => column !== undefined)
        .toSorted((a, b) => a.id.localeCompare(b.id));
    return {
        queryKey: ['stitched-columns', userViewKey, symbols, requested.map(columnDefinitionKey), columnOptions],
        queryFn: async ({ signal }, debugIdentifier?: string): Promise<FetchableTableCreationResultWithColumns> => {
            const idea: Idea = {
                name: 'Stitched columns',
                key: 'ticker',
                description: null,
                plan: { type: 'watchlist', includeList: symbols },
                defaultUniverseId: null,
                defaultColumnTemplateId: null,
                isTemplate: false,
            };
            const output = OutputStage.fromInputStage(
                IdeaStage.fromInputStage(SourceStage.allForKey('ticker'), idea, { userViewKey }),
                {},
                [symbolColumn, ...requested],
            );
            const result = await createTable(
                { ...output.intoTable(userViewKey), columnOptionContext: columnOptions },
                { signal },
                debugIdentifier,
            );
            return { ...result, fetchMode: 'snapshot' };
        },
        staleTime: 0,
        gcTime: 0,
    };
}
